/**
 * Shared per-triangle geometry over a flat positions array + 3-stride index
 * buffer. Consolidates the vertex / centroid / area / normal computations that
 * were duplicated across normals, plane-fit, region-grow/basis and capture.
 */
import type { Vec3 } from './types';
import { cross, length, normalize, sub, vertexAt } from './vec';

/** The 3 vertex positions of triangle `t`. */
export function triVertices(
  positions: ArrayLike<number>,
  indices: ArrayLike<number>,
  t: number,
): [Vec3, Vec3, Vec3] {
  return [
    vertexAt(positions, indices[t * 3]),
    vertexAt(positions, indices[t * 3 + 1]),
    vertexAt(positions, indices[t * 3 + 2]),
  ];
}

/** Centroid (mean of the 3 vertices) of triangle `t`. */
export function triCentroid(positions: ArrayLike<number>, indices: ArrayLike<number>, t: number): Vec3 {
  const [a, b, c] = triVertices(positions, indices, t);
  return [(a[0] + b[0] + c[0]) / 3, (a[1] + b[1] + c[1]) / 3, (a[2] + b[2] + c[2]) / 3];
}

/** Unit face normal of triangle `t` ([0,0,0] for a degenerate triangle). */
export function triNormal(positions: ArrayLike<number>, indices: ArrayLike<number>, t: number): Vec3 {
  const [a, b, c] = triVertices(positions, indices, t);
  return normalize(cross(sub(b, a), sub(c, a)));
}

/** World area of triangle `t`. */
export function triArea(positions: ArrayLike<number>, indices: ArrayLike<number>, t: number): number {
  const [a, b, c] = triVertices(positions, indices, t);
  return 0.5 * length(cross(sub(b, a), sub(c, a)));
}
